import { useState, useEffect } from "react";
import { FileText, LogOut, Plus, Pencil, Trash2, Eye, EyeOff, Mail, Play, Headphones, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { toast } from "sonner";
import PostEditor from "@/components/admin/PostEditor";
import SubscribersViewer from "@/components/admin/SubscribersViewer";
import AdminSkeleton from "@/components/admin/AdminSkeleton";

type Tab = "posts" | "subscribers";

interface Post {
  id: string;
  title: string;
  slug: string | null;
  excerpt: string | null;
  content: string | null;
  image_url: string | null;
  video_url: string | null;
  audio_url: string | null;
  category: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

const AdminDashboard = () => {
  const { user, isAdmin, isLoading } = useAdminAuth();
  const [activeTab, setActiveTab] = useState<Tab>("posts");
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchPosts();
    }
  }, [isAdmin]);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to load posts");
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm("Are you sure you want to delete this post?")) return;

    try {
      const { error } = await supabase
        .from("posts")
        .delete()
        .eq("id", postId);

      if (error) throw error;
      
      setPosts(posts.filter((p) => p.id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    }
  };

  const handleTogglePublish = async (post: Post) => {
    try {
      const newPublishedAt = post.published_at ? null : new Date().toISOString();
      
      const { error } = await supabase
        .from("posts")
        .update({ published_at: newPublishedAt })
        .eq("id", post.id);

      if (error) throw error;
      
      setPosts(posts.map((p) => 
        p.id === post.id ? { ...p, published_at: newPublishedAt } : p
      ));
      
      toast.success(newPublishedAt ? "Post published" : "Post unpublished");
    } catch (error) {
      console.error("Error toggling publish status:", error);
      toast.error("Failed to update post");
    }
  };

  const handleSavePost = async (postData: { 
    title: string; 
    slug: string;
    excerpt: string | null;
    content: string | null; 
    image_url: string | null; 
    video_url: string | null;
    audio_url: string | null;
    category: string | null; 
    published_at: string | null;
  }) => {
    try {
      if (editingPost) {
        // Update existing post
        const { error } = await supabase
          .from("posts")
          .update(postData)
          .eq("id", editingPost.id);

        if (error) throw error;
        toast.success("Post updated");
      } else {
        // Create new post
        const { error } = await supabase
          .from("posts")
          .insert([postData]);

        if (error) throw error;
        toast.success("Post created");
      }

      setEditingPost(null);
      setIsCreating(false);
      fetchPosts();
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error("Failed to save post");
    }
  };

  if (isLoading) {
    return <AdminSkeleton />;
  }

  if (!isAdmin) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-4 px-6">
        <div className="container mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-typewriter text-xl font-bold">
              Rebecca Ryell Travel
            </h1>
            <p className="text-sm text-primary-foreground">
              Command Centre
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-primary-foreground">
              {user?.email}
            </span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Show editor if creating or editing */}
        {(isCreating || editingPost) ? (
          <PostEditor
            post={editingPost}
            onSave={handleSavePost}
            onCancel={() => {
              setEditingPost(null);
              setIsCreating(false);
            }}
          />
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-8">
              <Button
                variant={activeTab === "posts" ? "default" : "outline"}
                onClick={() => setActiveTab("posts")}
                className="font-typewriter"
              >
                <FileText className="w-4 h-4 mr-2" />
                Field Notes
              </Button>
              <Button
                variant={activeTab === "subscribers" ? "default" : "outline"}
                onClick={() => setActiveTab("subscribers")}
                className="font-typewriter"
              >
                <Mail className="w-4 h-4 mr-2" />
                Subscribers
              </Button>
            </div>

            {/* Posts Tab */}
            {activeTab === "posts" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-typewriter text-2xl font-bold text-primary">
                    Field Notes & Mishaps
                  </h2>
                  <Button 
                    onClick={() => setIsCreating(true)}
                    className="bg-accent hover:bg-accent/90 font-typewriter"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Post
                  </Button>
                </div>

                {isLoadingPosts ? (
                  <p className="text-muted-foreground">Loading posts...</p>
                ) : posts.length === 0 ? (
                  <div className="text-center py-12 bg-card rounded-lg border border-border">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">No posts yet</p>
                    <Button onClick={() => setIsCreating(true)}>
                      Create your first post
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {posts.map((post) => (
                      <div 
                        key={post.id}
                        className="bg-card rounded-lg border border-border p-4 flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-typewriter font-bold text-primary truncate">
                              {post.title}
                            </h3>
                            {post.published_at ? (
                              <span className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full">
                                Published
                              </span>
                            ) : (
            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                Draft
                              </span>
                            )}
                            {post.video_url && (
                              <span className="inline-flex items-center gap-1 text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">
                                <Play className="w-3 h-3" /> Video
                              </span>
                            )}
                            {post.audio_url && (
                              <span className="inline-flex items-center gap-1 text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full">
                                <Headphones className="w-3 h-3" /> Audio
                              </span>
                            )}
                            {!post.video_url && !post.audio_url && (
                              <span className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                <BookOpen className="w-3 h-3" /> Article
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {post.category && `${post.category} • `}
                            Created {formatDate(post.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePublish(post)}
                            title={post.published_at ? "Unpublish" : "Publish"}
                          >
                            {post.published_at ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingPost(post)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Subscribers Tab */}
            {activeTab === "subscribers" && <SubscribersViewer />}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
